import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Grid,
  IconButton,
  InputLabel,
  Stack, TextField as MUITextField,
  Tooltip,
  Typography
} from '@mui/material'
import {TextField} from 'src/lib/itaptec/components_forms/src'
import React, {Dispatch, SetStateAction, useCallback, useEffect, useState} from 'react'
import {ITableColumn, TableList} from 'src/components/table'
import {ICON_MAS} from 'src/pages/sistema/components/SharedIcons'
import {EntityWithIdAndDescriptionFields, EntityWithIdFields, Moneda, MonedaFields} from 'src/types'
import CardActions from 'src/pages/sistema/components/CardActions'
import {CuentaProvisional, cuentaProvisional, CuentaProvisionalFields} from '../NuevoSistemaCorporate'
import DeleteIcon from '@mui/icons-material/Delete'
import {zodResolver} from '@hookform/resolvers/zod'
import {useForm, useWatch} from 'react-hook-form'
import {AsyncSelect} from 'src/components/forms'
import {HttpCacheOperacion} from 'src/pages/sistema/api/httpCacheOperacion'
import {OperacionCorporateImporteFields} from 'src/pages/sistema/types/importes'
import {OperacionCorporateCuentaFields} from '../types/fields'
import {HttpCacheGeneral} from 'src/http/httpCacheGeneral'
import {useConfirmationDialog} from 'src/hooks/useConfirmationDialog'
import {v4 as uuid} from 'uuid'

const AsyncLabelMoneda = ({codMoneda}: { codMoneda: number }) => {
  const [label, setLabel] = useState<string>()

  const loadLabel = () => HttpCacheGeneral.getMonedas()

  useEffect(() => {
    loadLabel().then(monedas => {
      const l = monedas.find(moneda => moneda[EntityWithIdFields.Id] === codMoneda)?.[
        EntityWithIdAndDescriptionFields.Description
        ]

      setLabel(l)
    })
  }, [])

  return !label ? <></> : <Typography>Moneda: {label}</Typography>
}

export const SeccionCorporateCuentasNuevo = ({
                                                idPais,
                                                cuentasProvisionales,
                                                setCuentasProvisionales
                                              }: {
  idPais: number
  cuentasProvisionales: CuentaProvisional[]
  setCuentasProvisionales: Dispatch<SetStateAction<CuentaProvisional[]>>
}) => {
  const {askConfirmation} = useConfirmationDialog()

  const columnsTablaCorporateDetalle: ITableColumn[] = [
    {
      label: 'Límite concertación',
      value: CuentaProvisionalFields.ImporteConcertacion,
      width: '33%',
      onRenderCell: (detalle: CuentaProvisional) => {
        return (
          <Typography>
            {`${
              detalle[CuentaProvisionalFields.CodMoneda] === 1
                ? '$'
                : detalle[CuentaProvisionalFields.CodMoneda] === 2
                  ? 'U$D'
                  : ''
            } ${detalle[CuentaProvisionalFields.ImporteConcertacion].toString()}`}
          </Typography>
        )
      }
    },
    {
      label: 'DVP',
      value: CuentaProvisionalFields.ImporteDPV,
      width: '33%',
      onRenderCell: (detalle: CuentaProvisional) => {
        return (
          <Typography>
            {`${
              detalle[CuentaProvisionalFields.CodMoneda] === 1
                ? '$'
                : detalle[CuentaProvisionalFields.CodMoneda] === 2
                  ? 'U$D'
                  : ''
            } ${detalle[CuentaProvisionalFields.ImporteDPV].toString()}`}
          </Typography>
        )
      }
    },
    {
      label: 'FOP',
      value: CuentaProvisionalFields.ImporteFOP,
      width: '33%',
      onRenderCell: (detalle: CuentaProvisional) => {
        return (
          <Typography>
            {`${
              detalle[CuentaProvisionalFields.CodMoneda] === 1
                ? '$'
                : detalle[CuentaProvisionalFields.CodMoneda] === 2
                  ? 'U$D'
                  : ''
            } ${detalle[CuentaProvisionalFields.ImporteFOP].toString()}`}
          </Typography>
        )
      }
    }
  ]

  const [openDialog, setOpenDialog] = useState<boolean>(false)
  const [monedas, setMonedas] = useState<Moneda[]>([])

  const {control, handleSubmit, watch, reset} = useForm<CuentaProvisional>({

    resolver: zodResolver(cuentaProvisional),

    defaultValues: {
      idProvisional: uuid(),
      [CuentaProvisionalFields.CodMoneda]: 2,

    }
  })


  const watchMoneda = useWatch({control: control, name: CuentaProvisionalFields.CodMoneda })

  useEffect(() => {
    HttpCacheGeneral.getMonedas().then(setMonedas)
  },[])

  const getMonedas = useCallback(() => Promise.resolve(monedas), [monedas])

  const alGuardarDetalleProvisional = (data: CuentaProvisional) => {
    const nuevoDetalle: CuentaProvisional = {
      ...data,
      idProvisional: uuid()
    }
    setCuentasProvisionales(prev => [...prev, nuevoDetalle])
    reset()
    setOpenDialog(false)
  }

  const monedaSeleccionada = watch(OperacionCorporateCuentaFields.CodMoneda)

  const getImportes = useCallback(() =>
          HttpCacheOperacion.getImportesCorporatePorMoneda(monedaSeleccionada)
    ,[monedaSeleccionada])

  return (
    <CardActions
      title={'Cuentas'}
      actions={
        <Button variant='contained' color='primary' endIcon={ICON_MAS} onClick={() => setOpenDialog(true)}>
          Nuevo
        </Button>
      }
      colapsable
    >
      {!cuentasProvisionales?.length ? (
        <Alert severity='info'>No se encontraron detalles para esta operación.</Alert>
      ) : (
        <Stack spacing={4}>
          {cuentasProvisionales.map(detalle => (
            <Card key={detalle[CuentaProvisionalFields.IdProvisional]}>
              <Box display='flex' justifyContent='flex-end'>
                <Tooltip title='Eliminar'>
                  <IconButton
                    onClick={() =>
                      askConfirmation(() =>
                        setCuentasProvisionales(dts =>
                          dts.filter(
                            dt =>
                              dt.idProvisional !==
                              detalle[CuentaProvisionalFields.IdProvisional]
                          )
                        )
                      )
                    }
                  >
                    <DeleteIcon color='error'/>
                  </IconButton>
                </Tooltip>
              </Box>
              <CardContent>
                <Box>
                  <Grid container spacing={2}>
                    <Grid item xs={12} md={4}>
                      <Typography variant='body2'>Número de cuenta</Typography>
                      <Typography variant='body1'>
                        {detalle[CuentaProvisionalFields.NroCuenta]}
                      </Typography>
                    </Grid>
                    <Grid item xs={12} md={4}>
                      <Typography variant='body2'>Razón social</Typography>
                      <Typography variant='body1'>
                        {detalle[CuentaProvisionalFields.RazonSocial]}
                      </Typography>
                    </Grid>
                    <Grid item xs={12} md={4}>
                      <Typography variant='body2'>Moneda</Typography>
                      <Typography variant='body1'>

                        {
                          monedas.find(moneda => moneda.id === detalle[CuentaProvisionalFields.CodMoneda])?.[EntityWithIdAndDescriptionFields.Description]
                          || '$'
                        }

                      </Typography>
                    </Grid>
                  </Grid>
                </Box>
              </CardContent>
              <Box>
                <TableList
                  entityList={[detalle]}
                  columns={columnsTablaCorporateDetalle}
                  isLoading={false}
                  error={false}
                />
              </Box>
            </Card>
          ))}
        </Stack>
      )}
      <Dialog open={openDialog} maxWidth='md' fullWidth>
        <DialogTitle>Nueva cuenta</DialogTitle>
        <DialogContent>
          <Stack spacing={4} pt={2}>
            <Grid container spacing={2}>
              <Grid item xs={12} md={4}>
                <TextField
                  control={control}
                  name={CuentaProvisionalFields.NroCuenta}
                  label='Nro Cuenta'
                  fullWidth
                  mask={{
                    mask: Number
                  }}
                  size='small'
                />
              </Grid>
              <Grid item xs={12} md={4}>
                <TextField
                  control={control}
                  name={CuentaProvisionalFields.RazonSocial}
                  label='Razón Social'
                  fullWidth
                  mask={{
                    mask: /^[A-Za-z]+[A-Za-z ]*$/
                  }}
                  size='small'
                />
              </Grid>
              <Grid item xs={12} md={4}>
                <MUITextField
                  label='Moneda'
                  value={monedas.find((moneda) => moneda[EntityWithIdFields.Id] === watchMoneda)?.[EntityWithIdAndDescriptionFields.Description]}
                  size={'small'}
                  disabled
                />
              </Grid>
            </Grid>
            <InputLabel>Límites</InputLabel>
            <Grid container spacing={2}>
              <Grid item xs={12} md={4}>
                <AsyncSelect
                  control={control}
                  name={OperacionCorporateCuentaFields.ImporteConcertacion}
                  label='Límite Concertación'
                  loadOptions={getImportes}
                  fullWidth
                  selectNotNulleable
                  valueField={OperacionCorporateImporteFields.CorporateImporte}


                />
              </Grid>
              <Grid item xs={12} md={4}>
                <AsyncSelect
                  control={control}
                  name={OperacionCorporateCuentaFields.ImporteDPV}
                  label='Límite DVP'
                  loadOptions={getImportes}
                  fullWidth
                  valueField={OperacionCorporateImporteFields.CorporateImporte}
                  selectNotNulleable

                />
              </Grid>
              <Grid item xs={12} md={4}>
                <AsyncSelect
                  control={control}
                  name={OperacionCorporateCuentaFields.ImporteFOP}
                  label='Límite FOP'
                  loadOptions={getImportes}
                  fullWidth
                  valueField={OperacionCorporateImporteFields.CorporateImporte}
                  selectNotNulleable

                />
              </Grid>
            </Grid>
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button variant='text' color='primary' onClick={() => setOpenDialog(false)}>
            Cancelar
          </Button>
          <Button variant='contained' color='primary' onClick={handleSubmit(alGuardarDetalleProvisional)}>
            Agregar
          </Button>
        </DialogActions>
      </Dialog>
    </CardActions>
  )
}
